import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ColumnEditorOptions } from 'primereact/column';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import {
  InputNumber,
  InputNumberValueChangeEvent
} from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

import { Employee, OperationAttainment } from '../../types/interfaces';
import { CustomDropdown } from '../../ui/custom-controls';
import { PickerTexts } from '../../utils';
import {
  attainmentsForOperation,
  mapEmployeeAttainments,
  type WorkOrderTimeframe
} from '../../utils/attainmentAvailability';

const OperationDropdownEditor: React.FC<{
  options: ColumnEditorOptions;
  values: any[] | undefined;
  placeholder: string;
  withBorderStyling: boolean;
  columnWidthPercent: number;
}> = ({ options, values, placeholder, withBorderStyling, columnWidthPercent }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  
  const calculateMaxWidth = useCallback(() => {
    if (!wrapperRef.current) return;
    
    const containerElement = wrapperRef.current.closest('.actualOperations-table');
    const tableElement = containerElement?.querySelector('.p-datatable') as HTMLElement || 
                         wrapperRef.current.closest('.p-datatable') as HTMLElement ||
                         containerElement as HTMLElement;
    
    if (tableElement) {
      const tableWidth = tableElement.getBoundingClientRect().width;
      const calculatedWidth = Math.max(0, (tableWidth * columnWidthPercent) / 100);
      setMaxWidth(calculatedWidth);
    }
  }, [columnWidthPercent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateMaxWidth();
    }, 0);
    
    const handleResize = () => {
      calculateMaxWidth();
    };
    
    window.addEventListener('resize', handleResize);
    let resizeObserver: ResizeObserver | null = null;
    
    const observerTimeoutId = setTimeout(() => {
      if (wrapperRef.current) {
        const containerElement = wrapperRef.current.closest('.actualOperations-table');
        const tableElement = containerElement?.querySelector('.p-datatable');
        
        if (containerElement) {
          resizeObserver = new ResizeObserver(handleResize);
          resizeObserver.observe(containerElement);
          if (tableElement) {
            resizeObserver.observe(tableElement);
          }
        }
      }
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(observerTimeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [calculateMaxWidth]);

  const borderColor = !options.value?.key ? '#e98181b8' : '#00800073';

  return (
    <div 
      ref={wrapperRef}
      className="actual-operations-operation-dropdown-wrapper"
      style={{ 
        width: maxWidth !== undefined ? `${maxWidth}px` : '100%',
        minWidth: 0,
        maxWidth: maxWidth !== undefined ? `${maxWidth}px` : '100%',
        overflow: 'hidden'
      }}
    >
      <Dropdown
        filter
        className="actual-operations-operation-dropdown"
        style={{
          borderColor: withBorderStyling ? borderColor : '',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}
        value={options.value}
        options={values}
        onChange={(e: DropdownChangeEvent) => options.editorCallback!(e.value)}
        placeholder={options.value.label || placeholder}
        itemTemplate={(option) => <div>{option.label}</div>}
      />
    </div>
  );
};

export const createDropdownEditor = <
  T extends { label: string; key: string | number }
>(
    options: ColumnEditorOptions,
    values: T[] | undefined,
    placeholder: string,
    withBorderStyling: boolean
  ) => {
  return (
    <OperationDropdownEditor
      options={options}
      values={values}
      placeholder={placeholder}
      withBorderStyling={withBorderStyling}
      columnWidthPercent={13}
    />
  );
};

export const executorEditor = (
  options: ColumnEditorOptions,
  department: any,
  availableEmployees: Employee[],
  plannedOperationsAttainments?: OperationAttainment[],
  rawEmployeesData?: Employee[],
  operationKey?: string | number,
  updateCounter?: number,
  allOperationsAttainments?: OperationAttainment[],
  currentOperationsAttainments?: OperationAttainment[],
  workOrderTimeframe?: WorkOrderTimeframe
) => {
  const effectiveOperationKey = operationKey !== undefined ? operationKey : options.rowData.operation?.key;
  
  return (
    <ExecutorEditor
      key={`${options.rowData.id}-${effectiveOperationKey || 'no-op'}-${updateCounter || 0}`}
      options={options}
      department={department}
      availableEmployees={availableEmployees}
      plannedOperationsAttainments={plannedOperationsAttainments}
      rawEmployeesData={rawEmployeesData}
      operationKey={effectiveOperationKey}
      updateCounter={updateCounter}
      allOperationsAttainments={allOperationsAttainments}
      currentOperationsAttainments={currentOperationsAttainments}
      workOrderTimeframe={workOrderTimeframe}
    />
  );
};

const ExecutorEditor: React.FC<{
  options: ColumnEditorOptions;
  department: any;
  availableEmployees: Employee[];
  plannedOperationsAttainments?: OperationAttainment[];
  rawEmployeesData?: Employee[];
  operationKey?: string | number;
  updateCounter?: number;
  allOperationsAttainments?: OperationAttainment[];
  currentOperationsAttainments?: OperationAttainment[];
  workOrderTimeframe?: WorkOrderTimeframe;
}> = ({ options, department, availableEmployees, plannedOperationsAttainments, rawEmployeesData: externalRawEmployeesData, operationKey: operationKeyFromProps, updateCounter, allOperationsAttainments, currentOperationsAttainments, workOrderTimeframe }) => {
  const [localRawEmployeesData, setLocalRawEmployeesData] = useState<Employee[]>([]);
  
  const rawEmployeesData = externalRawEmployeesData || localRawEmployeesData;

  const mapEmployeeAttainmentsForOperation = useCallback(
    (employees: Employee[], operationAttainments: OperationAttainment[] | undefined, operationKey: string | number | undefined): Employee[] =>
      mapEmployeeAttainments(
        employees,
        attainmentsForOperation(operationAttainments, operationKey),
        workOrderTimeframe
      ),
    [workOrderTimeframe]
  );

  useEffect(() => {
    if (externalRawEmployeesData && externalRawEmployeesData.length > 0) {
      setLocalRawEmployeesData(externalRawEmployeesData);
    } else if (availableEmployees.length > 0) {
      setLocalRawEmployeesData(availableEmployees);
    } else {
      setLocalRawEmployeesData([]);
    }
  }, [externalRawEmployeesData, availableEmployees]);

  const operationKey = operationKeyFromProps !== undefined 
    ? operationKeyFromProps 
    : options.rowData.operation?.key;
  
  const [employeesWithMappedAttainments, setEmployeesWithMappedAttainments] = useState<Employee[]>([]);
  const lastOperationKeyRef = React.useRef<string | number | undefined>(operationKey);

  useEffect(() => {
    const operationChanged = operationKey !== lastOperationKeyRef.current;
    
    if (operationChanged) {
      lastOperationKeyRef.current = operationKey;
    }

    const attainmentsToUse =
      currentOperationsAttainments && currentOperationsAttainments.length > 0
        ? currentOperationsAttainments
        : allOperationsAttainments && allOperationsAttainments.length > 0
          ? allOperationsAttainments
          : plannedOperationsAttainments;
    
    const mapped = mapEmployeeAttainmentsForOperation(
      rawEmployeesData,
      attainmentsToUse,
      operationKey
    );
    setEmployeesWithMappedAttainments(mapped);
  }, [
    operationKey,
    updateCounter,
    rawEmployeesData,
    allOperationsAttainments,
    plannedOperationsAttainments,
    currentOperationsAttainments,
    mapEmployeeAttainmentsForOperation
  ]);

  const selected = React.useMemo(() => Array.isArray(options.value) ? options.value : [], [options.value]);
  const currentSelectedIds = React.useMemo(() => new Set(selected.map(emp => emp.id)), [selected]);
  const effectiveSelected = React.useMemo(
    () => employeesWithMappedAttainments.filter(emp => currentSelectedIds.has(emp.id)),
    [employeesWithMappedAttainments, currentSelectedIds]
  );

  const borderColor = !selected || selected.length === 0 ? '#e98181b8' : '#00800073';

  return (
    <CustomDropdown<Employee>
      multiplySelectMode
      assetOptions={employeesWithMappedAttainments}
      selectedAsset={effectiveSelected}
      onSelectionChange={(e) => options.editorCallback!(e as unknown as Employee[])}
      fixedLabel={PickerTexts.executors(effectiveSelected.length)}
      passedClassName="actual-operations--employees-dropdown"
      style={{
        borderColor: borderColor,
        width: '100%'
      }}
      columns={[
        { field: 'fullName' as any, header: 'ФИО', width: '55%' },
        {
          field: 'id' as any,
          header: 'Навыки',
          width: '45%',
          body: (row: any) => {
            const { availableAttainments = [], unavailableAttainments = [] } = row;
            const operation = options.rowData.operation?.key;
            const hasSkills =
              availableAttainments.length > 0 || unavailableAttainments.length > 0;

            if (!operation) {
              return (
                <div className="flex items-center justify-center text-gray-500 italic text-sm py-2">
                  Фактическая работа не выбрана
                </div>
              );
            }
            if (!hasSkills) {
              return (
                <div className="flex items-center justify-center text-gray-500 italic text-sm py-2">
                  Навыки не требуются
                </div>
              );
            }

            return (
              <div className="flex flex-wrap gap-1">
                {availableAttainments.map((a: any) => (
                  <span
                    key={a.id}
                    style={{
                      backgroundColor: '#c6f6d5',
                      color: '#22543d',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '12px'
                    }}
                  >
                    {a.label}
                  </span>
                ))}
                {unavailableAttainments.map((u: any) => (
                  <span
                    key={u.id}
                    style={{
                      backgroundColor: '#fed7d7',
                      color: '#742a2a',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '12px'
                    }}
                  >
                    {u.label}
                  </span>
                ))}
              </div>
            );
          }
        }
      ]}
      emptyMessage="Нет сотрудников для выбранной операции."
      showClearOnHover
      overlayWidthMultiplier={1.8}
    />
  );
};

ExecutorEditor.displayName = 'ExecutorEditor';

export const textEditor = (
  options: ColumnEditorOptions,
  editorType: string,
  maxLength?: number
) => {
  if (editorType === 'number') {
    return (
      <InputNumber
        maxLength={maxLength}
        size={8}
        value={options.value || 0}
        onValueChange={(event: InputNumberValueChangeEvent) =>
          options.editorCallback!(event.target.value)
        }
      />
    );
  }
  return (
    <InputText
      className="w-full"
      type={editorType}
      value={options.value || ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback!(e.target.value)
      }
    />
  );
};
 